import { NextRequest, NextResponse } from 'next/server'

import { EncryptJWT, jwtDecrypt, JWTPayload } from 'jose'
import { env } from 'lib/env'

import { Session } from '@/lib/auth/auth-types'
import {
  decodePermissions,
  encodePermissions
} from '@/lib/auth/permissions-utils'

const getJWTKey = (): Buffer | null => {
  try {
    if (!env.JWT_SECRET_KEY) {
      console.warn('JWT_SECRET_KEY is not available - sessions will not work')
      return null
    }
    return Buffer.from(env.JWT_SECRET_KEY, 'base64')
  } catch (error) {
    console.error('Failed to create JWT key:', error)
    return null
  }
}

/**
 * Encrypts a session for storage in the cookie, encoding
 * role.permissions as compact indices instead of full strings to keep
 * the cookie under the browser's per-cookie size limit.
 */
export async function buildSessionCookie(session: Session): Promise<string> {
  const storedPayload = {
    ...session,
    admin: {
      ...session.admin,
      role: {
        ...session.admin.role,
        permissions: encodePermissions(session.admin.role.permissions)
      }
    }
  } as unknown as JWTPayload

  return encryptSession(storedPayload)
}

export async function decryptSession(
  session: string
): Promise<JWTPayload | null> {
  try {
    const key = getJWTKey()
    if (!key) {
      console.warn('Cannot decrypt session: JWT key not available')
      return null
    }

    const { payload } = await jwtDecrypt(session, key)
    return payload
  } catch (error) {
    console.error('Failed to decrypt session:', error)

    // Check if it's a JWT expiration error
    if (error && typeof error === 'object' && 'code' in error) {
      const jwtError = error as {
        claim?: string
        code: string
        reason?: string
      }
      if (jwtError.code === 'ERR_JWT_EXPIRED') {
        return null
      }
    }

    return null
  }
}

export async function encryptSession(payload: JWTPayload): Promise<string> {
  try {
    const key = getJWTKey()
    if (!key) {
      throw new Error('Cannot encrypt session: JWT key not available')
    }

    // Calculate expiration time based on the session's expires_in_minutes
    const session = payload as Session
    const expirationTime = session.expires_in_minutes
      ? `${session.expires_in_minutes}m`
      : '2h' // fallback to 2 hours

    const encryptedSession = await new EncryptJWT(payload)
      .setProtectedHeader({ alg: 'dir', enc: 'A256GCM' })
      .setIssuedAt()
      .setExpirationTime(expirationTime)
      .encrypt(key)

    return encryptedSession
  } catch (error) {
    console.error('Failed to encrypt session:', error)
    throw error
  }
}

// Utility function to get current time in seconds (UTC)
export async function getCurrentTimeInSeconds(): Promise<number> {
  try {
    return Math.floor(Date.now() / 1000)
  } catch (error) {
    console.error('Failed to get current time in seconds:', error)
    throw error
  }
}

/**
 * Decrypts a session cookie and decodes role.permissions back into
 * full permission strings.
 */
export async function parseSessionCookie(
  raw: string
): Promise<JWTPayload | null> {
  const payload = await decryptSession(raw)
  if (!payload) return null

  const stored = payload as unknown as Session
  const storedPermissions = stored.admin?.role
    ?.permissions as unknown as unknown[]

  // Sessions created before the permissions-as-indices change store full
  // permission strings instead of numeric indices. Decoding those as
  // indices silently drops them, so force these users to log back in.
  if (
    Array.isArray(storedPermissions) &&
    storedPermissions.length > 0 &&
    typeof storedPermissions[0] !== 'number'
  ) {
    console.warn(
      'Legacy string-based session permissions detected - forcing re-authentication'
    )
    return null
  }

  return {
    ...payload,
    admin: {
      ...stored.admin,
      role: {
        ...stored.admin.role,
        permissions: decodePermissions(
          storedPermissions as unknown as null | number[]
        )
      }
    }
  } as JWTPayload
}

// Helper function to clear session cookies and redirect to login

export async function redirectToLoginWithClearedSession(
  request: NextRequest
): Promise<NextResponse> {
  // Preserve the current route as a query parameter for redirect after login
  const loginUrl = new URL('/login', request.url)
  loginUrl.searchParams.set('redirect', request.nextUrl.pathname)

  const res = NextResponse.redirect(loginUrl)
  res.cookies.set({
    expires: new Date(0),
    httpOnly: true,
    name: 'session',
    path: '/',
    sameSite: 'strict',
    secure: process.env.NODE_ENV === 'production',
    value: ''
  })
  return res
}

export const isAuthPage = (pathname: string) => {
  return (
    pathname.includes('/login') ||
    pathname.includes('/sign-up') ||
    pathname.includes('/verify-email') ||
    pathname.includes('/forgot-password')
  )
}
